<script setup>
import { ref, watch } from 'vue';
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  canSimulate: { type: Boolean, default: false },
  runReplay: { type: Function, default: null },
  runAssistance: { type: Function, default: null },
});
const tab = ref('assistance');
const suggestion = ref(null);
const assisting = ref(false);
const assistError = ref('');
const messages = ref([]);
const running = ref(false);
const error = ref('');
let revision = 0;
watch(() => props.conversationId, () => {
  revision += 1;
  suggestion.value = null;
  assisting.value = false;
  assistError.value = '';
  messages.value = [];
  running.value = false;
  error.value = '';
});
async function simulate() {
  if (!props.canSimulate || !props.runReplay || running.value) return;
  const requestRevision = revision;
  const sourceId = props.conversationId;
  running.value = true;
  error.value = '';
  messages.value = [];
  try {
    const result = await props.runReplay(sourceId);
    if (requestRevision !== revision) return;
    if (!Array.isArray(result.simulated) || result.simulated.some(m =>
      !['user', 'assistant'].includes(m.role) || typeof m.content !== 'string')) {
      throw new Error('Invalid replay');
    }
    messages.value = result.simulated;
  } catch {
    if (requestRevision === revision) error.value = 'Não foi possível concluir. Nenhuma mensagem foi enviada ao cliente.';
  } finally {
    if (requestRevision === revision) running.value = false;
  }
}
async function assist() {
  if (assisting.value || !props.runAssistance) return;
  const requestRevision = revision;
  assisting.value = true;
  assistError.value = '';
  try {
    const result = await props.runAssistance(props.conversationId);
    if (requestRevision === revision) suggestion.value = result;
  } catch (e) {
    if (requestRevision === revision) assistError.value = e.response?.data?.error || 'Não foi possível gerar. Nenhuma mensagem foi enviada.';
  } finally {
    if (requestRevision === revision) assisting.value = false;
  }
}
</script>

<template>
  <aside class="w-[24rem] min-w-[19rem] max-w-[45%] shrink-0 flex flex-col min-h-0 p-4 gap-3 overflow-auto bg-n-surface-1 border-s border-n-weak text-n-slate-12" aria-label="VMetal">
    <header class="flex flex-wrap justify-between gap-2"><strong>VMetal</strong><span>Conversa {{ conversationId }}</span></header>
    <nav class="flex gap-2" aria-label="Modo da VMetal">
      <button class="px-3 py-2 border border-n-weak rounded-lg aria-pressed:bg-n-alpha-2" :aria-pressed="tab === 'assistance'" @click="tab = 'assistance'">Assistência</button>
      <button class="px-3 py-2 border border-n-weak rounded-lg aria-pressed:bg-n-alpha-2" :aria-pressed="tab === 'simulation'" @click="tab = 'simulation'">Simulação</button>
    </nav>
    <template v-if="tab === 'simulation'">
      <p>As mesmas mensagens do cliente, com respostas da VMetal. A conversa original permanece ao lado.</p>
      <button class="px-3 py-2 rounded-lg bg-n-blue-9 text-white disabled:opacity-50 disabled:cursor-not-allowed" :disabled="!canSimulate || !runReplay || running" @click="simulate">
        {{ running ? 'Simulando…' : 'Simular esta conversa' }}
      </button>
      <p v-if="!canSimulate">Simulação disponível para administradores.</p>
      <p v-else-if="!runReplay">Simulação ainda não conectada neste ambiente.</p>
      <p v-if="error" role="alert">{{ error }}</p>
      <div class="flex flex-col gap-3 flex-none min-h-[8rem]" aria-live="polite" :aria-busy="running">
        <article v-for="(message, index) in messages" :key="index" class="max-w-[88%] px-3 py-2 rounded-xl whitespace-pre-wrap break-words text-sm" :class="message.role === 'user' ? 'self-start bg-n-alpha-2' : 'self-end bg-n-blue-3'">
          <strong class="block text-xs mb-1">{{ message.role === 'user' ? 'Cliente' : 'VMetal' }}</strong>
          <div>{{ message.content }}</div>
        </article>
      </div>
      <details class="shrink-0"><summary>Como comparar</summary><p>Abra uma conversa e execute a simulação. Compare cada resposta com o atendimento original. As mensagens seguintes do cliente são fixas; não foram respostas à IA. Nenhuma mensagem desta área é enviada ao cliente.</p></details>
    </template>
    <section v-else class="flex flex-col gap-3 shrink-0">
      <p>Sugestões internas com base nesta conversa. Revise os fatos e adapte a resposta antes de usar.</p>
      <button class="px-3 py-2 rounded-lg bg-n-blue-9 text-white disabled:opacity-50" :disabled="assisting || !runAssistance" @click="assist">{{ assisting ? 'Analisando…' : 'Gerar ou atualizar sugestão' }}</button>
      <p v-if="assistError" role="alert">{{ assistError }}</p>
      <template v-if="suggestion">
        <article class="p-3 rounded-lg bg-n-alpha-2 break-words"><strong>Resumo</strong><p class="whitespace-pre-wrap">{{ suggestion.resumo }}</p></article>
        <label for="vmetal-draft" class="font-medium">Resposta sugerida · editável</label>
        <textarea id="vmetal-draft" v-model="suggestion.resposta" rows="6" class="w-full min-h-40 resize-y shrink-0 p-3 rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12" />
        <p class="text-sm">Selecione e copie o texto para o campo de resposta quando estiver de acordo. A VMetal não envia mensagens.</p>
        <article class="p-3 rounded-lg bg-n-alpha-2 break-words"><strong>Próxima ação</strong><p class="whitespace-pre-wrap">{{ suggestion.proxima_acao }}</p></article>
        <article v-if="suggestion.dados_faltantes.length" class="p-3 rounded-lg bg-n-alpha-2"><strong>Informações que podem ajudar</strong><ul class="list-disc ps-4"><li v-for="item in suggestion.dados_faltantes" :key="item" class="break-words">{{ item }}</li></ul></article>
        <p class="text-sm">Sugestão experimental. Atualize após novas mensagens; confirme preços, ERP e agenda nas fontes oficiais.</p>
      </template>
      <details class="shrink-0"><summary>Como usar a assistência</summary><p>Abra uma conversa, gere a sugestão e revise o resumo. Edite o rascunho se necessário. Copie somente o que decidir enviar para o campo de resposta da conversa. Exemplo fictício: o cliente pediu duas chapas; confirme a necessidade e encaminhe orçamento ao vendedor. Notas privadas e anexos não são usados nesta versão.</p></details>
    </section>
  </aside>
</template>

